interface SparklineProps {
  /** Oldest → newest. `null` = the sensor didn't report that sample. */
  values: (number | null)[];
  width?: number;
  height?: number;
  color?: string;
  fill?: string;
}

/** Inline SVG sparkline — no Plotly needed, instant render. */
export function Sparkline({
  values,
  width = 160,
  height = 36,
  color = "#c8102e",
  fill = "rgba(200,16,46,0.12)",
}: SparklineProps) {
  // Gaps are drawn as gaps. /api/latest used to send `value ?? 0` so a
  // missing sample rendered as a dive to zero, which also collapsed the
  // min/max range and flattened every real variation in the trace.
  const finite = values.filter(
    (v): v is number => v !== null && Number.isFinite(v),
  );

  if (finite.length < 2) {
    return (
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        <line x1={0} y1={height - 1} x2={width} y2={height - 1} stroke="#1f1f1f" strokeWidth={1} />
      </svg>
    );
  }

  const min = Math.min(...finite);
  const max = Math.max(...finite);
  const span = max - min || 1;
  const stepX = width / (values.length - 1);

  // Split into contiguous runs of real samples so both the stroke and
  // the fill break across gaps instead of bridging them.
  const runs: { x: number; y: number }[][] = [];
  let run: { x: number; y: number }[] = [];
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    if (v === null || v === undefined || !Number.isFinite(v)) {
      if (run.length > 0) runs.push(run);
      run = [];
      continue;
    }
    run.push({
      x: i * stepX,
      y: height - ((v - min) / span) * (height - 4) - 2,
    });
  }
  if (run.length > 0) runs.push(run);

  const toPath = (pts: { x: number; y: number }[]): string =>
    pts
      .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(2)},${p.y.toFixed(2)}`)
      .join(" ");

  const linePath = runs.map(toPath).join(" ");
  const fillPath = runs
    .filter((pts) => pts.length >= 2)
    .map(
      (pts) =>
        `${toPath(pts)} L${pts[pts.length - 1]!.x.toFixed(2)},${height} ` +
        `L${pts[0]!.x.toFixed(2)},${height} Z`,
    )
    .join(" ");

  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      {fillPath && <path d={fillPath} fill={fill} />}
      <path d={linePath} fill="none" stroke={color} strokeWidth={1.4} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}
